import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChainsTheme } from '../../context/ChainsThemeContext';
import { 
  Settings as SettingsIcon, Users, Shield, Bell, Globe, Database, 
  Key, Palette, Monitor, Smartphone, Wifi, Lock,
  Eye, EyeOff, Save, RefreshCw, Download, Upload,
  Trash2, Edit, Plus, X, Check, AlertTriangle,
  Sun, Moon, Zap, Clock, Calendar, MapPin, Truck,
  Workflow, GitBranch, GitCommit, GitPullRequest, GitMerge,
  ArrowRight, ArrowLeft, Play, Pause, Stop, RotateCcw,
  Filter, Search, List, Grid, BarChart3, PieChart,
  UserCheck
} from 'lucide-react';

const ChainsSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const { isDarkMode, toggleDarkMode } = useChainsTheme();
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    alerts: true
  });
  const [privacy, setPrivacy] = useState({
    dataSharing: false,
    analytics: true,
    location: true,
    thirdParty: false
  });
  const [workflows, setWorkflows] = useState([
    {
      id: 'purchase-approval',
      name: 'Purchase Request Approval',
      description: 'Automated approval workflow for purchase requests',
      status: 'active',
      steps: [
        { id: 1, name: 'Submit Request', type: 'manual', assignee: 'requester' },
        { id: 2, name: 'Manager Review', type: 'approval', assignee: 'manager', threshold: 50000 },
        { id: 3, name: 'Finance Review', type: 'approval', assignee: 'finance', threshold: 100000 },
        { id: 4, name: 'Final Approval', type: 'approval', assignee: 'director', threshold: 500000 },
        { id: 5, name: 'Procurement', type: 'action', assignee: 'procurement' }
      ],
      triggers: ['purchase_request_created'],
      conditions: ['amount > 1000'],
      autoStart: true
    },
    {
      id: 'inventory-alert',
      name: 'Low Stock Alert',
      description: 'Automated alerts for low inventory levels',
      status: 'active',
      steps: [
        { id: 1, name: 'Monitor Stock', type: 'automated', assignee: 'system' },
        { id: 2, name: 'Generate Alert', type: 'notification', assignee: 'inventory_manager' },
        { id: 3, name: 'Create PO', type: 'action', assignee: 'procurement' }
      ],
      triggers: ['stock_level_below_threshold'],
      conditions: ['quantity < min_stock'],
      autoStart: true
    },
    {
      id: 'quality-inspection',
      name: 'Quality Inspection',
      description: 'Quality control workflow for incoming goods',
      status: 'active',
      steps: [
        { id: 1, name: 'Receive Goods', type: 'manual', assignee: 'warehouse' },
        { id: 2, name: 'Initial Inspection', type: 'inspection', assignee: 'quality_team' },
        { id: 3, name: 'Detailed Testing', type: 'inspection', assignee: 'lab_technician' },
        { id: 4, name: 'Approval/Rejection', type: 'decision', assignee: 'quality_manager' }
      ],
      triggers: ['goods_received'],
      conditions: ['category in ["electronics", "chemicals"]'],
      autoStart: true
    }
  ]);

  // Approval workflow data from the better system
  const [approvalWorkflows, setApprovalWorkflows] = useState([
    {
      id: 'purchase-request',
      name: 'Purchase Request Workflow',
      type: 'Purchase Request',
      steps: [
        { role: 'Department Head', action: 'Review & Approve', required: true },
        { role: 'Finance Manager', action: 'Budget Check', required: true },
        { role: 'Procurement Manager', action: 'Final Approval', required: true }
      ],
      threshold: 50000,
      status: 'active'
    },
    {
      id: 'vendor-approval',
      name: 'Vendor Approval Workflow',
      type: 'Vendor Registration',
      steps: [
        { role: 'Procurement Manager', action: 'Initial Review', required: true },
        { role: 'Legal Team', action: 'Contract Review', required: true },
        { role: 'Finance Director', action: 'Final Approval', required: true }
      ],
      threshold: 0,
      status: 'active'
    },
    {
      id: 'contract-approval',
      name: 'Contract Approval Workflow',
      type: 'Contract Management',
      steps: [
        { role: 'Legal Manager', action: 'Legal Review', required: true },
        { role: 'Finance Director', action: 'Financial Review', required: true },
        { role: 'CEO', action: 'Final Approval', required: true }
      ],
      threshold: 1000000,
      status: 'active'
    }
  ]);

  const [approvalMatrix, setApprovalMatrix] = useState([
    {
      role: 'Department Head',
      permissions: ['Purchase Requests', 'Budget Approval', 'Vendor Review'],
      maxAmount: 25000,
      canDelegate: true
    },
    {
      role: 'Finance Manager',
      permissions: ['Budget Approval', 'Payment Authorization', 'Cost Analysis'],
      maxAmount: 100000,
      canDelegate: false
    },
    {
      role: 'Procurement Manager',
      permissions: ['Vendor Management', 'Contract Approval', 'Supplier Evaluation'],
      maxAmount: 500000,
      canDelegate: true
    },
    {
      role: 'Legal Manager',
      permissions: ['Contract Review', 'Legal Compliance', 'Risk Assessment'],
      maxAmount: 0,
      canDelegate: false
    },
    {
      role: 'Finance Director',
      permissions: ['Financial Approval', 'Budget Oversight', 'Investment Decisions'],
      maxAmount: 2000000,
      canDelegate: true
    },
    {
      role: 'CEO',
      permissions: ['Final Approval', 'Strategic Decisions', 'Board Reporting'],
      maxAmount: 10000000,
      canDelegate: true
    }
  ]);

  const settingsTabs = [
    { id: 'general', label: 'General', icon: SettingsIcon, description: 'Basic settings and preferences' },
    { id: 'workflows', label: 'Workflows', icon: Workflow, description: 'Manage automation and approval workflows' },
    { id: 'approval-workflows', label: 'Approval Workflows', icon: UserCheck, description: 'Configure approval hierarchies and workflows' },
    { id: 'users', label: 'Users & Permissions', icon: Users, description: 'Manage team access and roles' },
    { id: 'security', label: 'Security', icon: Shield, description: 'Security and authentication settings' },
    { id: 'notifications', label: 'Notifications', icon: Bell, description: 'Notification preferences' },
    { id: 'integrations', label: 'Integrations', icon: Globe, description: 'Third-party integrations' },
    { id: 'data', label: 'Data & Backup', icon: Database, description: 'Data management and backup' },
  ];

  const SettingCard = ({ title, description, children, icon: Icon }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-start space-x-4">
        <div className={`p-3 rounded-xl ${
          isDarkMode ? 'bg-white/10' : 'bg-gray-100'
        }`}>
          <Icon className={`w-6 h-6 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
        </div>
        <div className="flex-1">
          <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{description}</p>
          {children}
        </div>
      </div>
    </motion.div>
  );

  const ToggleSwitch = ({ enabled, onChange, label }) => (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>{label}</span>
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled 
            ? 'bg-gradient-to-r from-blue-500 to-cyan-600' 
            : isDarkMode ? 'bg-white/20' : 'bg-gray-300'
        }`}
      >
        <motion.span
          layout
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </motion.button>
    </div>
  );

  const InputField = ({ label, value, onChange, type = 'text', placeholder }) => (
    <div className="space-y-2">
      <label className={`text-sm font-medium ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${
          isDarkMode 
            ? 'bg-white/10 border border-white/20 text-white placeholder-white/40' 
            : 'bg-white/50 border border-gray-200 text-gray-900 placeholder-gray-500'
        }`}
      />
    </div>
  );

  const SelectField = ({ label, value, onChange, options }) => (
    <div className="space-y-2">
      <label className={`text-sm font-medium ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
        {label}
      </label>
      <select
        value={value}
        onChange={onChange}
        className={`w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${
          isDarkMode 
            ? 'bg-white/10 border border-white/20 text-white' 
            : 'bg-white/50 border border-gray-200 text-gray-900'
        }`}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="p-6 space-y-8 h-full overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Settings</h1>
          <p className={`text-lg ${isDarkMode ? 'text-white/60' : 'text-gray-700'}`}>Configure your supply chain preferences</p>
        </div>
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-6 py-3 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            <RefreshCw className="w-5 h-5 mr-2 inline" />
            Reset
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all"
          >
            <Save className="w-5 h-5 mr-2 inline" />
            Save Changes
          </motion.button>
        </div>
      </motion.div>

      {/* Tabs */}
      <div className={`flex space-x-1 p-1 backdrop-blur-sm rounded-xl border ${
        isDarkMode ? 'border-white/20' : 'border-gray-200'
      }`}>
        {settingsTabs.map((tab) => (
          <motion.button
            key={tab.id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg'
                : isDarkMode 
                  ? 'text-white/70 hover:text-white hover:bg-white/10'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'general' && (
          <motion.div
            key="general"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <SettingCard
              title="Theme & Appearance"
              description="Customize the look and feel of your supply chain interface"
              icon={Palette}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Dark Mode</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Switch between light and dark themes</p>
                  </div>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={toggleDarkMode}
                    className={`p-3 rounded-xl transition-all ${
                      isDarkMode 
                        ? 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                        : 'bg-white/50 border border-gray-200 text-gray-700 hover:bg-white/70'
                    }`}
                  >
                    {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  </motion.button>
                </div>
                
                <SelectField
                  label="Language"
                  value="en"
                  onChange={() => {}}
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'ar', label: 'العربية' },
                    { value: 'fr', label: 'Français' },
                    { value: 'es', label: 'Español' }
                  ]}
                />

                <SelectField
                  label="Time Zone"
                  value="UTC+3"
                  onChange={() => {}}
                  options={[
                    { value: 'UTC+3', label: 'Riyadh (UTC+3)' },
                    { value: 'UTC+0', label: 'London (UTC+0)' },
                    { value: 'UTC-5', label: 'New York (UTC-5)' },
                    { value: 'UTC+8', label: 'Singapore (UTC+8)' }
                  ]}
                />

                <SelectField
                  label="Currency"
                  value="SAR"
                  onChange={() => {}}
                  options={[
                    { value: 'SAR', label: 'Saudi Riyal (SAR)' },
                    { value: 'USD', label: 'US Dollar (USD)' },
                    { value: 'EUR', label: 'Euro (EUR)' },
                    { value: 'GBP', label: 'British Pound (GBP)' }
                  ]}
                />
              </div>
            </SettingCard>

            <SettingCard
              title="Performance & Optimization"
              description="Configure system performance and optimization settings"
              icon={Zap}
            >
              <div className="space-y-4">
                <ToggleSwitch
                  enabled={true}
                  onChange={() => {}}
                  label="Auto-refresh data every 5 minutes"
                />
                
                <ToggleSwitch
                  enabled={false}
                  onChange={() => {}}
                  label="Enable real-time notifications"
                />
                
                <ToggleSwitch
                  enabled={true}
                  onChange={() => {}}
                  label="Cache frequently accessed data"
                />
                
                <ToggleSwitch
                  enabled={false}
                  onChange={() => {}}
                  label="Enable advanced analytics"
                />
              </div>
            </SettingCard>
          </motion.div>
        )}

        {activeTab === 'workflows' && (
          <motion.div
            key="workflows"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <SettingCard
              title="Workflow Management"
              description="Configure automated workflows and approval processes"
              icon={Workflow}
            >
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Active Workflows</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{workflows.length} workflows configured</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    <Plus className="w-4 h-4 mr-2 inline" />
                    Create Workflow
                  </motion.button>
                </div>

                <div className="space-y-4">
                  {workflows.map((workflow) => (
                    <motion.div
                      key={workflow.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`p-4 rounded-xl border ${
                        isDarkMode ? 'bg-white/5 border-white/20' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{workflow.name}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              workflow.status === 'active' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {workflow.status}
                            </span>
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{workflow.description}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                            }`}
                          >
                            <Play className="w-4 h-4 text-green-500" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                            }`}
                          >
                            <Edit className="w-4 h-4 text-blue-500" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                            }`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </motion.button>
                        </div>
                      </div>

                      {/* Workflow Steps */}
                      <div className="space-y-2">
                        <p className={`text-xs font-medium ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Workflow Steps:</p>
                        <div className="flex items-center space-x-2">
                          {workflow.steps.map((step, index) => (
                            <React.Fragment key={step.id}>
                              <div className={`px-3 py-1 rounded-lg text-xs font-medium ${
                                isDarkMode ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {step.name}
                              </div>
                              {index < workflow.steps.length - 1 && (
                                <ArrowRight className="w-3 h-3 text-gray-400" />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      {/* Triggers and Conditions */}
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <p className={`text-xs font-medium ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Triggers:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {workflow.triggers.map((trigger, index) => (
                              <span key={index} className={`px-2 py-1 rounded text-xs ${
                                isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {trigger}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className={`text-xs font-medium ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Conditions:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {workflow.conditions.map((condition, index) => (
                              <span key={index} className={`px-2 py-1 rounded text-xs ${
                                isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {condition}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </SettingCard>

            <SettingCard
              title="Workflow Analytics"
              description="Monitor workflow performance and execution metrics"
              icon={BarChart3}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-xl ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                        <Play className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Executions Today</p>
                        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>1,247 workflows</p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Success Rate</p>
                        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>94.2%</p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Avg. Duration</p>
                        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>2.3 minutes</p>
                      </div>
                    </div>
                  </div>
                </div>

                <ToggleSwitch
                  enabled={true}
                  onChange={() => {}}
                  label="Enable workflow performance monitoring"
                />
                
                <ToggleSwitch
                  enabled={true}
                  onChange={() => {}}
                  label="Send alerts for failed workflows"
                />
                
                <ToggleSwitch
                  enabled={false}
                  onChange={() => {}}
                  label="Auto-retry failed workflows"
                />
              </div>
            </SettingCard>
          </motion.div>
        )}

        {activeTab === 'approval-workflows' && (
          <motion.div
            key="approval-workflows"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <SettingCard
              title="Approval Workflow Configuration"
              description="Define approval workflows and thresholds for different processes"
              icon={UserCheck}
            >
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Active Approval Workflows</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{approvalWorkflows.length} workflows configured</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    <Plus className="w-4 h-4 mr-2 inline" />
                    New Approval Workflow
                  </motion.button>
                </div>

                <div className="space-y-4">
                  {approvalWorkflows.map((workflow) => (
                    <motion.div
                      key={workflow.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`p-4 rounded-xl border ${
                        isDarkMode ? 'bg-white/5 border-white/20' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{workflow.name}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              workflow.status === 'active' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {workflow.status}
                            </span>
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{workflow.type}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                            }`}
                          >
                            <Edit className="w-4 h-4 text-blue-500" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                            }`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </motion.button>
                        </div>
                      </div>

                      {/* Approval Steps */}
                      <div className="space-y-2">
                        <p className={`text-xs font-medium ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Approval Steps:</p>
                        <div className="space-y-2">
                          {workflow.steps.map((step, index) => (
                            <div key={index} className={`flex items-center space-x-3 p-3 rounded-lg ${
                              isDarkMode ? 'bg-white/10' : 'bg-gray-100'
                            }`}>
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
                              }`}>
                                <span className={`text-xs font-medium ${
                                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                                }`}>{index + 1}</span>
                              </div>
                              <div className="flex-1">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{step.role}</p>
                                <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{step.action}</p>
                              </div>
                              {step.required && (
                                <Check className={`w-4 h-4 ${
                                  isDarkMode ? 'text-green-400' : 'text-green-600'
                                }`} />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-white/10">
                        <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                          Threshold: SAR {workflow.threshold.toLocaleString()}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </SettingCard>

            <SettingCard
              title="Approval Matrix"
              description="Configure approval hierarchies and permissions for different roles"
              icon={Shield}
            >
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Role-Based Approvals</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{approvalMatrix.length} roles configured</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    <Plus className="w-4 h-4 mr-2 inline" />
                    Add Role
                  </motion.button>
                </div>

                <div className={`rounded-xl border overflow-hidden ${
                  isDarkMode ? 'bg-white/5 border-white/20' : 'bg-white border-gray-200'
                }`}>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${
                        isDarkMode ? 'bg-white/10' : 'bg-gray-50'
                      }`}>
                        <tr>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-white/60' : 'text-gray-500'
                          }`}>
                            Role
                          </th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-white/60' : 'text-gray-500'
                          }`}>
                            Permissions
                          </th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-white/60' : 'text-gray-500'
                          }`}>
                            Max Amount
                          </th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-white/60' : 'text-gray-500'
                          }`}>
                            Can Delegate
                          </th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-white/60' : 'text-gray-500'
                          }`}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${
                        isDarkMode ? 'divide-white/10' : 'divide-gray-200'
                      }`}>
                        {approvalMatrix.map((role) => (
                          <tr key={role.role} className={`hover:bg-opacity-50 ${
                            isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                          }`}>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <UserCheck className={`w-4 h-4 mr-2 ${
                                  isDarkMode ? 'text-white/40' : 'text-gray-400'
                                }`} />
                                <span className={`text-sm font-medium ${
                                  isDarkMode ? 'text-white' : 'text-gray-900'
                                }`}>{role.role}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-1">
                                {role.permissions.map((permission) => (
                                  <span key={permission} className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                                    isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {permission}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`text-sm ${
                                isDarkMode ? 'text-white' : 'text-gray-900'
                              }`}>
                                SAR {role.maxAmount.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {role.canDelegate ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <X className="w-4 h-4 text-gray-400" />
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className={`p-1.5 rounded-lg transition-colors ${
                                    isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                                  }`}
                                >
                                  <Edit className="w-4 h-4 text-blue-500" />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className={`p-1.5 rounded-lg transition-colors ${
                                    isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                                  }`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </motion.button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </SettingCard>
          </motion.div>
        )}

        {activeTab === 'users' && (
          <motion.div
            key="users"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <SettingCard
              title="User Management"
              description="Manage team members and their access permissions"
              icon={Users}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Active Users</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>24 team members</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    <Plus className="w-4 h-4 mr-2 inline" />
                    Add User
                  </motion.button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-xl ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center">
                        <Users className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Administrators</p>
                        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>3 users</p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                        <Shield className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Managers</p>
                        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>8 users</p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                        <Monitor className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Operators</p>
                        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>12 users</p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-slate-600 rounded-full flex items-center justify-center">
                        <Eye className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Viewers</p>
                        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>1 user</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SettingCard>
          </motion.div>
        )}

        {activeTab === 'security' && (
          <motion.div
            key="security"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <SettingCard
              title="Authentication & Security"
              description="Configure security settings and authentication methods"
              icon={Shield}
            >
              <div className="space-y-4">
                <ToggleSwitch
                  enabled={true}
                  onChange={() => {}}
                  label="Two-factor authentication (2FA)"
                />
                
                <ToggleSwitch
                  enabled={true}
                  onChange={() => {}}
                  label="Session timeout after 30 minutes"
                />
                
                <ToggleSwitch
                  enabled={false}
                  onChange={() => {}}
                  label="Require password change every 90 days"
                />
                
                <ToggleSwitch
                  enabled={true}
                  onChange={() => {}}
                  label="Login attempt notifications"
                />

                <InputField
                  label="Session Timeout (minutes)"
                  value="30"
                  onChange={() => {}}
                  type="number"
                  placeholder="30"
                />
              </div>
            </SettingCard>

            <SettingCard
              title="Data Privacy"
              description="Manage data privacy and sharing preferences"
              icon={Lock}
            >
              <div className="space-y-4">
                <ToggleSwitch
                  enabled={privacy.dataSharing}
                  onChange={() => setPrivacy({...privacy, dataSharing: !privacy.dataSharing})}
                  label="Allow data sharing for analytics"
                />
                
                <ToggleSwitch
                  enabled={privacy.analytics}
                  onChange={() => setPrivacy({...privacy, analytics: !privacy.analytics})}
                  label="Enable usage analytics"
                />
                
                <ToggleSwitch
                  enabled={privacy.location}
                  onChange={() => setPrivacy({...privacy, location: !privacy.location})}
                  label="Share location data for logistics"
                />
                
                <ToggleSwitch
                  enabled={privacy.thirdParty}
                  onChange={() => setPrivacy({...privacy, thirdParty: !privacy.thirdParty})}
                  label="Allow third-party integrations"
                />
              </div>
            </SettingCard>
          </motion.div>
        )}

        {activeTab === 'notifications' && (
          <motion.div
            key="notifications"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <SettingCard
              title="Notification Preferences"
              description="Configure how and when you receive notifications"
              icon={Bell}
            >
              <div className="space-y-4">
                <ToggleSwitch
                  enabled={notifications.email}
                  onChange={() => setNotifications({...notifications, email: !notifications.email})}
                  label="Email notifications"
                />
                
                <ToggleSwitch
                  enabled={notifications.push}
                  onChange={() => setNotifications({...notifications, push: !notifications.push})}
                  label="Push notifications"
                />
                
                <ToggleSwitch
                  enabled={notifications.sms}
                  onChange={() => setNotifications({...notifications, sms: !notifications.sms})}
                  label="SMS notifications"
                />
                
                <ToggleSwitch
                  enabled={notifications.alerts}
                  onChange={() => setNotifications({...notifications, alerts: !notifications.alerts})}
                  label="Critical alerts"
                />

                <SelectField
                  label="Notification Frequency"
                  value="immediate"
                  onChange={() => {}}
                  options={[
                    { value: 'immediate', label: 'Immediate' },
                    { value: 'hourly', label: 'Hourly Digest' },
                    { value: 'daily', label: 'Daily Summary' },
                    { value: 'weekly', label: 'Weekly Report' }
                  ]}
                />
              </div>
            </SettingCard>
          </motion.div>
        )}

        {activeTab === 'integrations' && (
          <motion.div
            key="integrations"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <SettingCard
              title="Third-Party Integrations"
              description="Connect with external services and platforms"
              icon={Globe}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-xl border ${
                    isDarkMode ? 'bg-white/5 border-white/20' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Wifi className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>ERP System</p>
                          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Connected</p>
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl border ${
                    isDarkMode ? 'bg-white/5 border-white/20' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                          <Truck className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Logistics API</p>
                          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Connected</p>
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl border ${
                    isDarkMode ? 'bg-white/5 border-white/20' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                          <Database className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Analytics Platform</p>
                          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Disconnected</p>
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl border ${
                    isDarkMode ? 'bg-white/5 border-white/20' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                          <Bell className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Notification Service</p>
                          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Connected</p>
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </SettingCard>
          </motion.div>
        )}

        {activeTab === 'data' && (
          <motion.div
            key="data"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <SettingCard
              title="Data Management"
              description="Manage your data, backups, and storage settings"
              icon={Database}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-xl text-center ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <Database className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>2.4 GB</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Data Used</p>
                  </div>

                  <div className={`p-4 rounded-xl text-center ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <Download className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Last: 2h ago</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Backup</p>
                  </div>

                  <div className={`p-4 rounded-xl text-center ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                  }`}>
                    <Clock className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Daily</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Auto Backup</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    <Download className="w-4 h-4 mr-2 inline" />
                    Export Data
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-4 py-2 backdrop-blur-sm border rounded-lg transition-all ${
                      isDarkMode 
                        ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                        : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
                    }`}
                  >
                    <Upload className="w-4 h-4 mr-2 inline" />
                    Import Data
                  </motion.button>
                </div>

                <ToggleSwitch
                  enabled={true}
                  onChange={() => {}}
                  label="Automatic daily backups"
                />
                
                <ToggleSwitch
                  enabled={false}
                  onChange={() => {}}
                  label="Compress backup files"
                />
                
                <ToggleSwitch
                  enabled={true}
                  onChange={() => {}}
                  label="Encrypt backup data"
                />
              </div>
            </SettingCard>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChainsSettings; 